import type { BookingDetails } from "@/app/booking-calendar/bookingCalendarApi"
import type { ReleaseNotes } from "@/app/booking-calendar/components/actions/types"
import { SITE_TIME_ZONE, longDateLabel, minutesToLabel, zonedDateAndMinutes } from "@/app/booking-calendar/lib/siteTime"

/**
 * What a customer is told when staff release their slot because the person they were booked with is
 * unavailable (AB#769, Q14). Sent as the cancellation's note for the customer, through the staff
 * cancel and CMS's own cancellation message — nothing new on the server.
 *
 * NEEDS HUMAN REVIEW (decision ams-ghb): machine-drafted wording, used verbatim until MSC approves or
 * replaces it. `{date}` is the appointment's Pacific date and time, `{site}` the office.
 */
export const RELEASE_NOTICE_TEMPLATE =
  "We are sorry: your appointment on {date} at {site} has to be cancelled because the staff member is unavailable. Please book again online or call us to pick a new time."

/** Said for `{site}` when the booking's site has no name. */
const UNNAMED_SITE = "our office"

/**
 * The release's internal note. On an override's detail it names the override; in the new-override
 * preview there is no override yet, so it does not.
 */
export function releaseInternalNote(overrideId?: string): string {
  return overrideId ? `Released: host unavailable (override ${overrideId})` : "Released: host unavailable"
}

/** The customer notice for one booking: "…your appointment on Wednesday, Oct 14 at 1:00 PM at Federal Way…". */
export function releaseNotice(booking: Pick<BookingDetails, "start" | "siteName">, timeZone: string = SITE_TIME_ZONE): string {
  const { date, minutes } = zonedDateAndMinutes(booking.start, timeZone)
  const site = booking.siteName?.trim() || UNNAMED_SITE

  return RELEASE_NOTICE_TEMPLATE
    .replace("{date}", `${longDateLabel(date)} at ${minutesToLabel(minutes)}`)
    .replace("{site}", site)
}

/** Both notes a release starts with, for `AffectedBookingsList`'s `releaseNotes`. */
export function releaseNotes(
  booking: Pick<BookingDetails, "start" | "siteName">,
  overrideId?: string,
  timeZone: string = SITE_TIME_ZONE,
): ReleaseNotes {
  return {
    internalNote: releaseInternalNote(overrideId),
    externalNote: releaseNotice(booking, timeZone),
  }
}
